// URL-synced state for the Activity page (filters + window).
//
// The URL is the source of truth. Reads parse on every render (cheap);
// writes go through ``setSearchParams`` so back/forward and shareable
// links work naturally.

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { ActivityFilters, ActivityWindow } from "@/services/api/me";

const WINDOWS: readonly ActivityWindow[] = ["7d", "30d", "90d"] as const;

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function parseFloatOrNull(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseIntOrNull(raw: string | null): number | null {
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

export interface ActivityUiState {
  filters: ActivityFilters;
  window: ActivityWindow;
  /** Set the entire filter set at once (writes URL). Pass {} to clear. */
  setFilters: (next: ActivityFilters) => void;
  /** Patch one or more filter fields without touching the rest. */
  patchFilters: (patch: Partial<ActivityFilters>) => void;
  setWindow: (next: ActivityWindow) => void;
  clearFilters: () => void;
  /** Add a value to a multi-select filter if not already present. */
  toggleRouteClass: (rc: string) => void;
  toggleSubAgent: (sa: string) => void;
}

export function useActivityFilters(): ActivityUiState {
  const [params, setParams] = useSearchParams();

  const filters: ActivityFilters = useMemo(
    () => ({
      routeClasses: parseCsv(params.get("route_class")),
      subAgents: parseCsv(params.get("sub_agent")),
      since: params.get("since") || null,
      until: params.get("until") || null,
      minGrounding: parseFloatOrNull(params.get("min_grounding")),
      maxGrounding: parseFloatOrNull(params.get("max_grounding")),
      refineOnly: params.get("refine_only") === "1" || params.get("refine_only") === "true",
      minCost: params.get("min_cost") || null,
      maxCost: params.get("max_cost") || null,
      minLatencyMs: parseIntOrNull(params.get("min_latency_ms")),
      maxLatencyMs: parseIntOrNull(params.get("max_latency_ms")),
      errorOnly: params.get("error_only") === "1" || params.get("error_only") === "true",
    }),
    [params],
  );

  const window: ActivityWindow = useMemo(() => {
    const raw = params.get("window");
    return WINDOWS.includes(raw as ActivityWindow) ? (raw as ActivityWindow) : "30d";
  }, [params]);

  const writeFilters = useCallback(
    (next: ActivityFilters, windowOverride?: ActivityWindow) => {
      const sp = new URLSearchParams();
      const win = windowOverride ?? window;
      if (win !== "30d") sp.set("window", win);
      if (next.routeClasses?.length) sp.set("route_class", next.routeClasses.join(","));
      if (next.subAgents?.length) sp.set("sub_agent", next.subAgents.join(","));
      if (next.since) sp.set("since", next.since);
      if (next.until) sp.set("until", next.until);
      if (next.minGrounding != null) sp.set("min_grounding", String(next.minGrounding));
      if (next.maxGrounding != null) sp.set("max_grounding", String(next.maxGrounding));
      if (next.refineOnly) sp.set("refine_only", "1");
      if (next.minCost) sp.set("min_cost", next.minCost);
      if (next.maxCost) sp.set("max_cost", next.maxCost);
      if (next.minLatencyMs != null) sp.set("min_latency_ms", String(next.minLatencyMs));
      if (next.maxLatencyMs != null) sp.set("max_latency_ms", String(next.maxLatencyMs));
      if (next.errorOnly) sp.set("error_only", "1");
      setParams(sp, { replace: false });
    },
    [setParams, window],
  );

  const setFilters = useCallback((next: ActivityFilters) => writeFilters(next), [writeFilters]);

  const patchFilters = useCallback(
    (patch: Partial<ActivityFilters>) => writeFilters({ ...filters, ...patch }),
    [filters, writeFilters],
  );

  const setWindow = useCallback(
    (next: ActivityWindow) => writeFilters(filters, next),
    [filters, writeFilters],
  );

  const clearFilters = useCallback(() => writeFilters({}), [writeFilters]);

  const toggleRouteClass = useCallback(
    (rc: string) => {
      const cur = new Set(filters.routeClasses ?? []);
      cur.has(rc) ? cur.delete(rc) : cur.add(rc);
      writeFilters({ ...filters, routeClasses: Array.from(cur) });
    },
    [filters, writeFilters],
  );

  const toggleSubAgent = useCallback(
    (sa: string) => {
      const cur = new Set(filters.subAgents ?? []);
      cur.has(sa) ? cur.delete(sa) : cur.add(sa);
      writeFilters({ ...filters, subAgents: Array.from(cur) });
    },
    [filters, writeFilters],
  );

  return {
    filters,
    window,
    setFilters,
    patchFilters,
    setWindow,
    clearFilters,
    toggleRouteClass,
    toggleSubAgent,
  };
}
